export const CONTACT_INTELLIGENCE_KEY = 'contact_intelligence' as const;

export const CONTACT_INTELLIGENCE_TRIGGER_KEYS = [
  'formEntryLinked',
  'callLinked',
  'noteAddedOrLinked',
  'leadWon',
  'leadLost',
  'leadReopened',
] as const;

export type ContactIntelligenceTriggerKey = (typeof CONTACT_INTELLIGENCE_TRIGGER_KEYS)[number];

export interface ContactIntelligenceSettings {
  triggers: Record<ContactIntelligenceTriggerKey, boolean>;
}

export const DEFAULT_CONTACT_INTELLIGENCE_SETTINGS: ContactIntelligenceSettings = {
  triggers: {
    formEntryLinked: true,
    callLinked: true,
    noteAddedOrLinked: true,
    leadWon: true,
    leadLost: true,
    leadReopened: true,
  },
};

export const CONTACT_INTELLIGENCE_TRIGGER_OPTIONS: Array<{
  key: ContactIntelligenceTriggerKey;
  label: string;
  description: string;
}> = [
  {
    key: 'formEntryLinked',
    label: 'Form entry linked',
    description: 'Refresh when a Form Entry becomes linked to the Contact.',
  },
  {
    key: 'callLinked',
    label: 'Call linked',
    description: 'Refresh when a Call becomes linked to the Contact.',
  },
  {
    key: 'noteAddedOrLinked',
    label: 'Note added or linked',
    description: 'Refresh for Contact notes and notes on linked Calls or Form Entries.',
  },
  {
    key: 'leadWon',
    label: 'Linked Lead won',
    description: 'Refresh when a linked Lead moves to Converted.',
  },
  {
    key: 'leadLost',
    label: 'Linked Lead lost',
    description: 'Refresh when a linked Lead moves to Lost.',
  },
  {
    key: 'leadReopened',
    label: 'Linked Lead reopened',
    description: 'Refresh when a Converted or Lost Lead returns to an active status.',
  },
];

export function normalizeContactIntelligenceSettings(value: unknown): ContactIntelligenceSettings {
  const input = isRecord(value) && isRecord(value.triggers) ? value.triggers : {};
  const defaults = DEFAULT_CONTACT_INTELLIGENCE_SETTINGS.triggers;
  return {
    triggers: {
      formEntryLinked: booleanValue(input.formEntryLinked, defaults.formEntryLinked),
      callLinked: booleanValue(input.callLinked, defaults.callLinked),
      noteAddedOrLinked: booleanValue(input.noteAddedOrLinked, defaults.noteAddedOrLinked),
      leadWon: booleanValue(input.leadWon, defaults.leadWon),
      leadLost: booleanValue(input.leadLost, defaults.leadLost),
      leadReopened: booleanValue(input.leadReopened, defaults.leadReopened),
    },
  };
}

export function isContactIntelligenceTriggerEnabled(
  settings: ContactIntelligenceSettings,
  triggerType: string,
): boolean {
  if (triggerType === 'manual_refresh') return true;
  if (triggerType === 'form_entry_linked') return settings.triggers.formEntryLinked;
  if (triggerType === 'call_linked') return settings.triggers.callLinked;
  if (
    triggerType === 'contact_note_added'
    || triggerType === 'call_note_added'
    || triggerType === 'form_entry_note_added'
  ) return settings.triggers.noteAddedOrLinked;
  if (triggerType === 'lead_won') return settings.triggers.leadWon;
  if (triggerType === 'lead_lost') return settings.triggers.leadLost;
  if (triggerType === 'lead_reopened') return settings.triggers.leadReopened;
  return false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function booleanValue(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
